import { ComponentInterface } from '@stencil/core';

export function jsonWatcher<T>(
  prop: string,
  value: T | string
): {
  prop: string;
  value: T;
} {
  if (typeof prop !== 'string') {
    return null;
  }

  if (value && typeof value === 'string') {
    try {
      const json = JSON.parse(value);

      return {
        prop,
        value: json
      };
    } catch (e) {
      console.error(`Error parsing JSON for prop '${prop}'!`);
      console.error(e);

      return {
        prop,
        value: undefined
      };
    }
  }

  return {
    prop,
    // @ts-ignore
    value
  };
}

export function registerClickOutside(
  component: ComponentInterface,
  element: HTMLElement,
  callback: () => void
): void {
  window.addEventListener('click', (e: Event) => {
    if (e.composedPath()?.indexOf(element) < 0) {
      callback.call(component);
    }
  });
}

export function onFocusWithTab(clb: () => void) {
  return function () {
    const detectTab = (e: KeyboardEvent) => {
      if (e.key === 'Tab' || e.key === 'Shift') {
        clb();
      }
      this?.removeEventListener('keyup', detectTab);
    };
    this?.addEventListener('keyup', detectTab);
  };
}

export enum ActivityMapRegion {
  Header = 'service-header',
  SecondaryNavigation = 'service-subnav',
  Footer = 'service-footer'
}

// As the analytics script can't access the shadow DOM elements for click tracking,
// we emit an event on menuitem click which the analytics script can listen for.
export function emitClickTrackerEvent(
  payload: {
    componentName: ActivityMapRegion;
    title: string;
    url: string;
    originEvent?: any;
  },
  options?: {
    customEventName?: string;
    hasUiActions?: boolean;
  }
): void {
  const deprecatedEventName = 'cxsdsHeaderClickTracker';
  const { customEventName: eventName = 'cxsdsComponentClickTracker', hasUiActions = true } =
    options ?? {};
  // in case we need to open a menu or perform any other ui action we need to give ui time to do it, otherwise
  // it will wait until event is fired and handled
  const wrapper = hasUiActions ? (fn) => setTimeout(fn, 5) : (fn) => fn();
  // Launch's custom event listener will only listen for
  // custom event broadcasts starting at document.body!
  wrapper(() => {
    const eventPayload = payload
      ? {
          ...payload,
          componentName: `${payload.componentName}-${process.env.npm_package_version}`
        }
      : payload;
    emitCustomEvent(eventName, eventPayload);
    // This part was added to avoid situations when the existing AA script handle event not from the header but from the secondary nav
    if (payload?.componentName === ActivityMapRegion.Header) {
      emitCustomEvent(deprecatedEventName, eventPayload);
    }
  });
}

export function emitCustomEvent(eventName: string, payload?: any): void {
  const customEvent = new CustomEvent(eventName, {
    detail: payload,
    bubbles: true
  });

  document.body.dispatchEvent(customEvent);
}

export function camelCase(string: string): string {
  const words = string
    .trim()
    .replace(/[\s_-]+/g, '-')
    .split('-');

  if (words.length === 1) {
    const word = words[0];
    if (word === word.toUpperCase()) {
      return word.toLowerCase();
    }
  } else {
    return words.reduce((accumulator, word, index) => {
      const currentWord = word.toLowerCase();
      return index === 0
        ? accumulator + currentWord.charAt(0).toLowerCase() + currentWord.slice(1)
        : accumulator + currentWord.charAt(0).toUpperCase() + currentWord.slice(1);
    }, '');
  }

  return string.charAt(0).toLowerCase() + string.slice(1);
}

export function toDashCase(string: string): string {
  const words = string
    .trim()
    .replace(/[\s_-]+/g, '-')
    .split('-');

  if (words.length === 1) {
    const word = words[0];
    if (word === word.toUpperCase()) {
      return word.toLowerCase();
    } else {
      return word
        .split(/(?=[A-Z])/)
        .join('-')
        .toLowerCase();
    }
  } else {
    return words.map((word) => word.toLowerCase()).join('-');
  }
}

export function hashCode(str: string): string {
  let hash = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    let chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return '' + hash;
}
